import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../data/cloud-data.js';
import { Configuration, AccentOptions } from '../../data/configuration.js';
import { ExteriorConfigurator } from '../../configurator/exterior-configurator.js';
import { VehicleView } from '../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../view-content.js';
import { CameraController, CameraViews } from '../../camera-controller.js';
import SelectedSectionItem from '../ui/selected-section-item.tsx';
import ViewContentRows from '../ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface FinalFinishesViewProps {
	exteriorConfigurator: ExteriorConfigurator;
	onBackButtonClicked: () => void;
	cameraController: CameraController;
}

interface FinalFinishesViewContextType {
	selectedSplitterItemId: string;
	handleSplitterItemClicked: (id: string) => void;
	selectedAccentItemId: string;
	handleAccentItemClicked: (id: string) => void;
	selectedPaintProtectionItemId: string;
	handlePaintProtectionItemClicked: (id: string) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function FinalFinishesView(props: FinalFinishesViewProps) {

	// initial values.
	const initialSplitterItemId = Configuration.splitterOption;
	const initialAccentItemId = checkAccentPackSelected() ? Configuration.accentPackOption : Configuration.accentOption;
	const initialPaintProtectionItemId = Configuration.paintProtectionFilmOption;

	// states.
	const [selectedSplitterItemId, setSelectedSplitterItemId] = useState(initialSplitterItemId);
	const [selectedAccentItemId, setSelectedAccentItemId] = useState(initialAccentItemId);
	const [selectedPaintProtectionItemId, setSelectedPaintProtectionItemId] = useState(initialPaintProtectionItemId);

	// handlers.

	const handlePaintProtectionItemClicked = (id: string) => {

		// toggle the item.
		if (id === selectedPaintProtectionItemId) {
			id = 'NONE';
		}

		// update the selected item and item name.
		setSelectedPaintProtectionItemId(id);

		// update the configuration.
		Configuration.paintProtectionFilmOption = id;
		Configuration.updateCosts();
	}

	const handleSplitterItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedSplitterItemId(id);

		// update the configuration.
		Configuration.splitterOption = id;
		Configuration.updateCosts();

		props.exteriorConfigurator.selectSplitterStyle(id);

		// set the camera to the cowl viewpoint.
		props.cameraController.changeCameraView(CameraViews.CowlView);
	}

	const handleAccentItemClicked = (id: string) => {

		if (id === Configuration.accentOption || id === Configuration.accentPackOption) {
			id = AccentOptions.NoBadge;
			Configuration.accentPackOption = 'NONE';
		}

		// update the selected item and item name.
		setSelectedAccentItemId(id);

		// update the configuration - accent pack disables badge selection.
		if (id === AccentOptions.AccentPack) {
			Configuration.accentPackOption = id;
			Configuration.accentOption = AccentOptions.NoBadge;

			// set the camera to the accent pack viewpoint.
			props.cameraController.changeCameraView(CameraViews.AccentPackView);
		} else {
			Configuration.accentOption = id;
			Configuration.accentPackOption = 'NONE';

			// set the camera to the badge viewpoint.
			props.cameraController.changeCameraView(CameraViews.BadgeView);
		}

		Configuration.updateCosts();

		// select the accent style on the car.
		props.exteriorConfigurator.selectAccentStyle(id);
	}

	// show accent or badge price.
	const accentOptions = selectedAccentItemId === AccentOptions.AccentPack
		? CloudData.accessoryOptions
		: CloudData.badgeOptions;

	// return the HTML.
	return (
		<FinalFinishesViewContext.Provider value={{ selectedSplitterItemId, handleSplitterItemClicked, selectedAccentItemId, handleAccentItemClicked, selectedPaintProtectionItemId, handlePaintProtectionItemClicked }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-final-finishes">
							FINAL FINISHES
						</h2>
					</div>

					<SectionPanel
						subHeading={'SPLITTER/DIFFUSER'}
						modalContent={CloudData.getModalContent(selectedSplitterItemId, CloudData.accessoryOptions)}
					>
						<SelectedSectionItem
							itemName={getAccessoryItemName(selectedSplitterItemId)}
							sectionPrice={CloudData.getPrice(selectedSplitterItemId, CloudData.accessoryOptions, { ref: false })}
						/>
						<SplitterItems />
					</SectionPanel>

					<SectionPanel
						subHeading={'ACCENTS'}
						modalContent={CloudData.getModalContent(selectedAccentItemId, accentOptions)}
					>
						<SelectedSectionItem
							itemName={getAccentItemName(selectedAccentItemId)}
							sectionPrice={CloudData.getPrice(selectedAccentItemId, accentOptions, { ref: false })}
						/>
						<AccentItems />
					</SectionPanel>

					<SectionPanel
						subHeading={'PAINT PROTECTION'}
						modalContent={CloudData.getModalContent(selectedPaintProtectionItemId, CloudData.accessoryOptions)}
					>
						<SelectedSectionItem
							itemName={getAccessoryItemName(selectedPaintProtectionItemId)}
							sectionPrice={CloudData.getPrice(selectedPaintProtectionItemId, CloudData.accessoryOptions, { ref: false })}
						/>
						<PaintProtectionItems />
					</SectionPanel>

				</div>
			</VehicleView>
		</FinalFinishesViewContext.Provider>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const FinalFinishesViewContext = createContext<FinalFinishesViewContextType | undefined>(undefined);

function SplitterItems() {
	return (
		<ViewContentRows>
			<SplitterItem id={'SD001'} imageFilePath={'dark-satin'}/>
			<SplitterItem id={'SD002'} imageFilePath={'matching-body'}/>
		</ViewContentRows>
	);
}

function SplitterItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(FinalFinishesViewContext);
	if (!context) {
		throw new Error('FinalFinishesViewContext must be used within a FinalFinishesView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getAccessoryItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/exterior/final-finishes/${props.imageFilePath}.svg`}
			isSelected={props.id === context.selectedSplitterItemId}
			onItemClicked={context.handleSplitterItemClicked}
		/>
	);
}

function AccentItems() {

	// return the html.
	return(
		<ViewContentRows>
			<AccentItem id={'B001'} imageFilePath={'badge-black'} />
			<AccentItem id={'B002'} imageFilePath={'badge-colour'} />
			<AccentItem id={'EA001'} imageFilePath={'accent-pack-black'} />
		</ViewContentRows>
	);
}

function AccentItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(FinalFinishesViewContext);
	if (!context) {
		throw new Error('FinalFinishesViewContext must be used within a FinalFinishesView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getAccentItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/exterior/final-finishes/${props.imageFilePath}.svg`}
			isSelected={props.id === context.selectedAccentItemId}
			onItemClicked={context.handleAccentItemClicked}
		/>
	);
}

function PaintProtectionItems() {

	// return the html.
	return (
		<ViewContentRows>
			<PaintProtectionItem id={'BCSF007'} imageFilePath={'paint-protection-film'} />
		</ViewContentRows>
	);
}

function PaintProtectionItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(FinalFinishesViewContext);
	if (!context) {
		throw new Error('FinalFinishesViewContext must be used within a FinalFinishesView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getAccessoryItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/exterior/final-finishes/${props.imageFilePath}.svg`}
			isSelected={props.id === context.selectedPaintProtectionItemId}
			onItemClicked={context.handlePaintProtectionItemClicked}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getAccessoryItemName(id: string): string {
	return CloudData.getName(id, CloudData.accessoryOptions);
}

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getAccentItemName(id: string): string {

	let dataOptions = CloudData.badgeOptions;

	// use accessoryOptions for accent pack.
	if (id === AccentOptions.AccentPack) {
		dataOptions = CloudData.accessoryOptions;
	}

	return CloudData.getName(id, dataOptions);
}

// checks if accent pack is picked.
function checkAccentPackSelected(): boolean {
	return Configuration.accentPackOption !== 'NONE';
}
